"use client";

import type { LeadFlowRichTextContent } from "@homeinstead/schemas";
import type { PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";

import { RichText } from "@homeinstead/ui/components/RichText";
import { useAnswers } from "../../hooks/use-answers";
import { filterHiddenAnswerBlocks } from "../../utils/filter-hidden-answer-blocks";
import { useLeadFlowRuntime } from "../LeadFlowRuntimeContext";
import { AnswerPlaceholderMark } from "./AnswerPlaceholderMark";

// Module scope keeps the map referentially stable; a new map per render makes
// Portable Text remount every mark.
const LEAD_FLOW_PORTABLE_TEXT_COMPONENTS: Partial<PortableTextComponents> = {
  marks: { leadFlowAnswerPlaceholder: AnswerPlaceholderMark },
};

type LeadFlowPortableTextProps = {
  className?: string;
  value: LeadFlowRichTextContent | null | undefined;
};

/** Lead flow copy with office placeholders and answer placeholders resolved. */
function LeadFlowPortableText({ className, value }: LeadFlowPortableTextProps) {
  const answers = useAnswers();
  const { answerLabels, placeholderValues } = useLeadFlowRuntime();

  if (!value || value.length === 0) {
    return null;
  }

  const visibleValue = filterHiddenAnswerBlocks(value, answers, answerLabels);

  if (visibleValue.length === 0) {
    return null;
  }

  return (
    <RichText
      className={className}
      components={LEAD_FLOW_PORTABLE_TEXT_COMPONENTS}
      placeholders={placeholderValues}
      value={visibleValue as PortableTextBlock[]}
    />
  );
}

export { LeadFlowPortableText };
